/**
 * @fileoverview API Key schemas for extension authentication
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Valid scopes for API keys
 * Each scope grants access to specific endpoints
 */
export const API_KEY_SCOPES = ['capture', 'capture-queue', 'mcp'];

// Base ApiKey schema
const BaseApiKeySchema = z.object({
  id: z.string().openapi({
    description: 'Unique identifier for the API key (ULID)',
    example: '01HJQK5RXYZ123456789012345',
  }),
  name: z.string().min(1).max(100).openapi({
    description: 'User-friendly name for the API key',
    example: 'Chrome Extension',
  }),
  keyPrefix: z.string().length(8).openapi({
    description: 'First 8 characters of the key for identification',
    example: 'obn_abc1',
  }),
  keyHash: z.string().openapi({
    description: 'SHA-256 hash of the API key (never expose the actual key)',
  }),
  userId: z.string().uuid().openapi({
    description: 'ID of the user who owns this API key',
    example: '550e8400-e29b-41d4-a716-446655440000',
  }),
  scopes: z.array(z.enum(['capture', 'capture-queue', 'mcp'])).openapi({
    description: 'Scopes/permissions granted to this key',
    example: ['capture', 'capture-queue', 'mcp'],
  }),
  lastUsedAt: z.string().datetime().nullable().optional().openapi({
    description: 'ISO 8601 timestamp when the key was last used',
    example: '2024-01-15T10:30:00Z',
  }),
  createdAt: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the key was created',
    example: '2024-01-15T10:30:00Z',
  }),
  expiresAt: z.string().datetime().nullable().optional().openapi({
    description: 'ISO 8601 timestamp when the key expires (null for never)',
    example: '2025-01-15T10:30:00Z',
  }),
});

// Full ApiKey schema
export const ApiKeySchema = BaseApiKeySchema.openapi('ApiKey');

// Create schema - only need name, scopes are fixed for extension keys
export const CreateApiKeySchema = z
  .object({
    name: z.string().min(1).max(100).openapi({
      description: 'User-friendly name for the API key',
      example: 'Chrome Extension',
    }),
  })
  .openapi('CreateApiKey');

// Response schema - excludes the keyHash, includes the full key only on creation
export const ApiKeyResponseSchema = ApiKeySchema.omit({ keyHash: true }).openapi('ApiKeyResponse');

// Creation response - includes the actual key (only shown once)
export const ApiKeyCreatedResponseSchema = ApiKeyResponseSchema.extend({
  key: z.string().openapi({
    description: 'The actual API key (only shown once at creation time)',
    example: 'obn_abc12345xyz67890secret',
  }),
}).openapi('ApiKeyCreatedResponse');

export const ApiKeyListResponseSchema = createListResponseSchema(
  ApiKeyResponseSchema,
  'apiKeys',
  'ApiKeyListResponse'
);

/**
 * JSDoc type definition for ApiKey
 * @typedef {Object} ApiKeyType
 * @property {string} id - Unique identifier for the API key
 * @property {string} name - User-friendly name
 * @property {string} keyPrefix - First 8 chars of key for identification
 * @property {string} keyHash - SHA-256 hash of the key
 * @property {string} userId - ID of the owning user
 * @property {string[]} scopes - Granted permissions
 * @property {string|null|undefined} [lastUsedAt] - Last usage timestamp
 * @property {string} createdAt - Creation timestamp
 * @property {string|null|undefined} [expiresAt] - Expiration timestamp
 */

export const ApiKeyType = /** @type {ApiKeyType} */ ({});
